import React from 'react';
import { SparkIcon, Text, Toast } from '@spark';
import { formatScheduledAt } from '../utils/schedule';

const DURATION = 'about 10 minutes';

function ExternalLink({ href, children }) {
    return (
        <a className="platform-upgrader-link" href={href} target="_blank" rel="noreferrer">
            {children}
        </a>
    );
}

function Paragraph({ children }) {
    return (
        <Text as="p" variant="base-regular" color="secondary" className="platform-upgrader-paragraph">
            {children}
        </Text>
    );
}

/**
 * The changelog link, plus the infrastructure changelog for infra releases.
 * Every release's metadata carries an ``infra_changelog_url``; only ``release_type`` says whether it applies.
 */
function OfferLinks({ offer }) {
    return (
        <>
            {offer.changelog_url && (
                <Paragraph>
                    <ExternalLink href={offer.changelog_url}>See what&apos;s changed</ExternalLink>
                </Paragraph>
            )}
            {offer.release_type === 'infra' && offer.infra_changelog_url && (
                <Paragraph>
                    This version needs infrastructure changes before you update.{' '}
                    <ExternalLink href={offer.infra_changelog_url}>Read the infrastructure changelog</ExternalLink>
                </Paragraph>
            )}
        </>
    );
}

function availableContent({ offer, isAdmin, inAppUpgrade }) {
    let next;
    if (!isAdmin) {
        next = 'Speak to your admin about updating.';
    } else if (inAppUpgrade) {
        next = `Updating takes ${DURATION}, and the platform is read-only meanwhile. Update now or schedule it for later.`;
    } else {
        next = `To install it, run helm upgrade with the new chart version. Updating takes ${DURATION}.`;
    }
    return (
        <>
            <Paragraph>A new platform version is ready to install.</Paragraph>
            <Paragraph>{next}</Paragraph>
            <OfferLinks offer={offer} />
        </>
    );
}

/**
 * The top-right popup (Figma 0:7664, 0:7771, 0:7804, 0:7735). After an update,
 * What's new opens in the middle of the screen instead (Figma 91:12570).
 * Closing an available or scheduled popup moves it into the bell until the upgrader opens it again.
 */
export default function UpdatePopup({ upgrader }) {
    const { popup, offer, isAdmin, inAppUpgrade, schedule, error } = upgrader;
    if (!popup) return null;

    const common = {
        detailed: true,
        showIconButton: true,
        onDismissClick: upgrader.closePopup,
    };
    let toast;

    if (popup === 'error') {
        toast = (
            <Toast
                {...common}
                feedbackType="warning"
                title="Error while updating"
                content={
                    <Paragraph>
                        {error || 'Something went wrong while updating the platform. Please contact support.'}
                    </Paragraph>
                }
                showButtons
                showCloseButton={false}
                actionLabel="Contact support"
                onActionClick={upgrader.contactSupport}
            />
        );
    } else if (popup === 'scheduled') {
        toast = (
            <Toast
                {...common}
                icon={<SparkIcon slug="clock-hour-icon" size="medium" />}
                title="Update Scheduled"
                content={
                    <>
                        <Paragraph>
                            {`The platform update is scheduled for ${formatScheduledAt(schedule.at)}. The platform will be read-only for ${DURATION}.`}
                        </Paragraph>
                        {isAdmin && schedule.scheduled_by !== 'guest' && (
                            <Paragraph>{`Scheduled by ${schedule.scheduled_by}.`}</Paragraph>
                        )}
                    </>
                }
                showButtons={isAdmin}
                closeLabel="Cancel update"
                actionLabel="Reschedule"
                onCloseClick={upgrader.cancelSchedule}
                onActionClick={upgrader.openSchedule}
            />
        );
    } else {
        const withButtons = isAdmin && inAppUpgrade;
        toast = (
            <Toast
                {...common}
                icon={<SparkIcon slug="bell" size="medium" />}
                title="Platform Update Available"
                content={availableContent({ offer, isAdmin, inAppUpgrade })}
                showButtons={withButtons}
                closeLabel="Update now"
                actionLabel="Schedule"
                onCloseClick={upgrader.startUpdateFlow}
                onActionClick={upgrader.openSchedule}
            />
        );
    }

    return <div className="platform-upgrader-popup">{toast}</div>;
}
